import React, { useMemo } from "react";
import {
  EuiPanel,
  EuiFlexGroup,
  EuiFlexItem,
  EuiText,
  EuiStat,
  EuiProgress,
  EuiSpacer,
  EuiIcon,
  EuiBadge,
  EuiHorizontalRule,
  EuiAccordion,
} from "@elastic/eui";
import { useAnketaI18n } from "src/internationalization";
import { OpsanioApplianceData } from "..";
import { OpsanioWaves } from "src/questionnaires";
import {
  deriveFleetHealth,
  DerivedHealth,
  EMPTY_HEALTH,
  HealthStatus,
} from "../health";
import { FleetHealthEntry, UseFleetHealthResult } from "../useFleetHealth";
import { useChartColors } from "../useChartColors";
import {
  FleetStatusStackedBar,
  OpenIncidentsBar,
  WaveDistributionDonut,
  AgentsBar,
} from "./charts";
import { relativeLabel } from "../format";
import { waveColor, WAVE_ORDER } from "../waves";

const i18n = "anketa.opsanio.overview";

/**
 * High-end customer health cockpit for the appliance fleet.
 *
 * Top: the "Customer Health Overview" card (live, PRTG-derived). Below: a chart
 * grid mixing live monitoring (fleet status, open incidents) with real
 * provisioning analytics (wave distribution, agents). All values are honestly
 * derived - unmonitored appliances are shown as such, never as healthy, and the
 * card surfaces monitoring coverage so the numbers can't be misread.
 */
export const OpsanioApplianceOverview = ({
  filtered,
  health,
  onShowAttention,
}: {
  /** Filter the table to critical + warning (the "need attention" card). */
  onShowAttention?: () => void;
  /** Active filtered subset - all aggregates and charts reflect this. */
  filtered: OpsanioApplianceData[];
  /**
   * Live fleet health, owned by the page so the filter bar and the table can
   * read the same sweep instead of triggering a second one.
   */
  health: UseFleetHealthResult;
}): JSX.Element => {
  const { translate } = useAnketaI18n();
  const colors = useChartColors();
  const { byId, loading, loaded, progress } = health;

  // Health for the filtered subset only (unfetched/archived → unmonitored,
  // never counted as healthy). Drives the card KPIs and the fleet-status donut.
  const visibleHealth = useMemo<DerivedHealth[]>(
    () => filtered.map((a) => byId[a.id]?.health ?? EMPTY_HEALTH),
    [filtered, byId],
  );
  const fleet = useMemo(
    () => deriveFleetHealth(visibleHealth),
    [visibleHealth],
  );

  // byId restricted to the filtered subset, for the open-incidents chart.
  const visibleById = useMemo<Record<string, FleetHealthEntry>>(() => {
    const out: Record<string, FleetHealthEntry> = {};
    for (const a of filtered) {
      if (byId[a.id]) out[a.id] = byId[a.id];
    }
    return out;
  }, [filtered, byId]);

  // Real provisioning analytics from the summary payload (no PRTG needed).
  const provisioning = useMemo(() => {
    const waveCounts: Record<string, number> = {};
    const agentRows: Array<{ label: string; agents: number; wave: string }> =
      [];
    filtered.forEach((a) => {
      if (a.wave) waveCounts[a.wave] = (waveCounts[a.wave] ?? 0) + 1;
      agentRows.push({
        label: a.displayName || a.id,
        agents: a.common?.counts?.agents ?? 0,
        wave: (a.wave as OpsanioWaves) ?? "",
      });
    });
    return { waveCounts, agentRows };
  }, [filtered]);

  // How many appliances the open incidents are spread over.
  const incidentAppliances = useMemo(
    () => visibleHealth.filter((h) => h.activeIncidents > 0).length,
    [visibleHealth],
  );

  const onlineCount = useMemo(
    () => visibleHealth.filter((h) => h.online).length,
    [visibleHealth],
  );

  // Support & engagement totals across the filtered subset (open GitLab issues,
  // open OTOBO tickets, Sentry user feedback). 0 where an integration is
  // unconfigured/unreachable - never fabricated.
  const engagement = useMemo(() => {
    let gitlabOpen = 0;
    let otoboOpen = 0;
    let feedback = 0;
    for (const a of filtered) {
      const e = byId[a.id]?.engagement;
      if (e) {
        gitlabOpen += e.gitlabOpen;
        otoboOpen += e.otoboOpen;
        feedback += e.feedback;
      }
    }
    return { gitlabOpen, otoboOpen, feedback };
  }, [filtered, byId]);

  const statusLabel: Record<HealthStatus, string> = {
    healthy: translate(`${i18n}.status.healthy`, { defaultMessage: "Healthy" }),
    warning: translate(`${i18n}.status.warning`, { defaultMessage: "Warning" }),
    critical: translate(`${i18n}.status.critical`, {
      defaultMessage: "Critical",
    }),
    unknown: translate(`${i18n}.status.unmonitored`, {
      defaultMessage: "Unmonitored",
    }),
  };

  const initialLoading = loading && !loaded;
  const coveragePct =
    fleet.total > 0 ? Math.round((100 * fleet.monitored) / fleet.total) : 0;

  /** Appliances a human should look at now: any non-healthy monitored signal. */
  const attention = fleet.statusCounts.critical + fleet.statusCounts.warning;

  // Demoted, not dropped: every KPI that lost its card lives on here.
  const secondaryStats: Array<{ label: string; value: string }> = [
    {
      label: translate(`${i18n}.kpi.score`, {
        defaultMessage: "Fleet health score",
      }),
      value: fleet.averageScore !== null ? `${fleet.averageScore}%` : "-",
    },
    {
      label: translate(`${i18n}.kpi.activeAppliances`, {
        defaultMessage: "Active appliances",
      }),
      value: `${onlineCount}`,
    },
    {
      label: translate(`${i18n}.kpi.lastSync`, { defaultMessage: "Last sync" }),
      value: relativeLabel(fleet.lastSync, translate),
    },
    {
      label: translate(`${i18n}.kpi.total`, {
        defaultMessage: "Total appliances",
      }),
      value: `${fleet.total}`,
    },
    {
      label: statusLabel.healthy,
      value: `${fleet.statusCounts.healthy}`,
    },
    {
      label: statusLabel.unknown,
      value: `${fleet.statusCounts.unknown}`,
    },
    {
      label: translate(`${i18n}.kpi.gitlabIssues`, {
        defaultMessage: "Open GitLab issues",
      }),
      value: `${engagement.gitlabOpen}`,
    },
    {
      label: translate(`${i18n}.kpi.feedback`, {
        defaultMessage: "User feedback",
      }),
      value: `${engagement.feedback}`,
    },
  ];

  const statusOrder: HealthStatus[] = ["healthy", "warning", "critical", "unknown"];
  const waveEntries = WAVE_ORDER.filter((w) => provisioning.waveCounts[w]).map(
    (w) => ({ wave: w, count: provisioning.waveCounts[w] }),
  );

  const kpiTitle = (text: string, icon: string, iconColor?: string) => (
    <EuiFlexGroup gutterSize="xs" alignItems="center" responsive={false}>
      <EuiFlexItem grow={false}>
        <EuiIcon type={icon} size="s" color={iconColor ?? "subdued"} />
      </EuiFlexItem>
      <EuiFlexItem grow={false}>
        <EuiText size="xs" color="subdued">
          {text}
        </EuiText>
      </EuiFlexItem>
    </EuiFlexGroup>
  );

  return (
    <EuiFlexGroup direction="column" gutterSize="m">
      {/* ---- Four action-driving figures ---- */}
      <EuiFlexItem>
        <EuiFlexGroup gutterSize="m" wrap>
          <EuiFlexItem style={{ minWidth: 200 }}>
            {/* Clicking filters the table to exactly these appliances. */}
            <EuiPanel
              hasBorder
              paddingSize="m"
              onClick={onShowAttention}
              aria-label={translate(`${i18n}.kpi.attention.filter`, {
                defaultMessage: "Show appliances that need attention",
              })}
            >
              {kpiTitle(
                translate(`${i18n}.kpi.attention`, {
                  defaultMessage: "Need attention",
                }),
                "alert",
                attention > 0 ? "danger" : undefined,
              )}
              <EuiSpacer size="s" />
              <EuiStat
                title={`${attention}`}
                description=""
                titleColor={
                  fleet.statusCounts.critical > 0
                    ? "danger"
                    : fleet.statusCounts.warning > 0
                      ? "warning"
                      : "default"
                }
                titleSize="m"
                isLoading={initialLoading}
              />
              <EuiText size="xs" color="subdued">
                {translate(`${i18n}.kpi.attention.sub`, {
                  values: {
                    critical: fleet.statusCounts.critical,
                    warning: fleet.statusCounts.warning,
                  },
                  defaultMessage: "{{critical}} critical, {{warning}} warning",
                })}
              </EuiText>
            </EuiPanel>
          </EuiFlexItem>
          <EuiFlexItem style={{ minWidth: 200 }}>
            <EuiPanel hasBorder paddingSize="m">
              {kpiTitle(
                translate(`${i18n}.kpi.incidents`, {
                  defaultMessage: "Active incidents",
                }),
                "visPulse",
              )}
              <EuiSpacer size="s" />
              <EuiStat
                title={`${fleet.activeIncidents}`}
                description=""
                titleColor={fleet.activeIncidents > 0 ? "danger" : "default"}
                titleSize="m"
                isLoading={initialLoading}
              />
              <EuiText size="xs" color="subdued">
                {translate(`${i18n}.kpi.incidents.appliances`, {
                  values: { count: incidentAppliances },
                  defaultMessage: "in {{count}} appliances",
                })}
              </EuiText>
            </EuiPanel>
          </EuiFlexItem>
          <EuiFlexItem style={{ minWidth: 200 }}>
            <EuiPanel hasBorder paddingSize="m">
              {kpiTitle(
                translate(`${i18n}.kpi.coverage`, {
                  defaultMessage: "Monitoring coverage",
                }),
                "eye",
              )}
              <EuiSpacer size="s" />
              <EuiStat
                title={`${coveragePct} %`}
                description=""
                titleSize="m"
                isLoading={initialLoading}
              />
              <EuiFlexGroup gutterSize="s" alignItems="center" responsive={false}>
                <EuiFlexItem>
                  <EuiProgress
                    value={coveragePct}
                    max={100}
                    color="primary"
                    size="s"
                  />
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiText size="xs" color="subdued">
                    {translate(`${i18n}.kpi.coverage.of`, {
                      values: { monitored: fleet.monitored, total: fleet.total },
                      defaultMessage: "{{monitored}} of {{total}}",
                    })}
                  </EuiText>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiPanel>
          </EuiFlexItem>
          <EuiFlexItem style={{ minWidth: 200 }}>
            <EuiPanel hasBorder paddingSize="m">
              {kpiTitle(
                translate(`${i18n}.kpi.openTickets`, {
                  defaultMessage: "Open tickets",
                }),
                "editorComment",
              )}
              <EuiSpacer size="s" />
              <EuiStat
                title={`${engagement.otoboOpen + engagement.gitlabOpen}`}
                description=""
                titleSize="m"
                isLoading={initialLoading}
              />
              <EuiText size="xs" color="subdued">
                {translate(`${i18n}.kpi.openTickets.sub`, {
                  values: {
                    otobo: engagement.otoboOpen,
                    gitlab: engagement.gitlabOpen,
                  },
                  defaultMessage: "{{otobo}} OTOBO · {{gitlab}} GitLab issues",
                })}
              </EuiText>
            </EuiPanel>
          </EuiFlexItem>
        </EuiFlexGroup>
        {initialLoading && (
          <>
            <EuiSpacer size="s" />
            <EuiProgress
              value={progress.done}
              max={progress.total || 1}
              size="xs"
              color="primary"
            />
            <EuiText size="xs" color="subdued">
              {translate(`${i18n}.loading`, {
                values: { done: progress.done, total: progress.total },
                defaultMessage: "Loading live health… {{done}}/{{total}}",
              })}
            </EuiText>
          </>
        )}
      </EuiFlexItem>

      {/* ---- Fleet status + waves in one strip; details on demand ---- */}
      <EuiFlexItem>
        <EuiPanel hasBorder paddingSize="m">
          <EuiFlexGroup gutterSize="l" alignItems="center" wrap>
            <EuiFlexItem grow={false}>
              <EuiText size="s">
                <strong>
                  {translate(`${i18n}.fleetStatus`, {
                    defaultMessage: "Fleet status",
                  })}
                </strong>
              </EuiText>
            </EuiFlexItem>
            <EuiFlexItem style={{ minWidth: 280 }}>
              <div
                role="img"
                aria-label={statusOrder
                  .map((st) => `${statusLabel[st]} ${fleet.statusCounts[st]}`)
                  .join(", ")}
                style={{
                  display: "flex",
                  gap: 2,
                  height: 10,
                  borderRadius: 5,
                  overflow: "hidden",
                  background: colors.grid,
                }}
              >
                {statusOrder
                  .filter((st) => fleet.statusCounts[st] > 0)
                  .map((st) => (
                    <div
                      key={st}
                      style={{
                        flexGrow: fleet.statusCounts[st],
                        background: colors.health[st],
                      }}
                    />
                  ))}
              </div>
              <EuiSpacer size="xs" />
              <EuiFlexGroup gutterSize="m" responsive={false} wrap>
                {statusOrder.map((st) => (
                  <EuiFlexItem grow={false} key={st}>
                    <EuiText size="xs" color="subdued">
                      <span
                        aria-hidden="true"
                        style={{
                          display: "inline-block",
                          width: 8,
                          height: 8,
                          borderRadius: 2,
                          marginRight: 6,
                          background: colors.health[st],
                        }}
                      />
                      {statusLabel[st]} {fleet.statusCounts[st]}
                    </EuiText>
                  </EuiFlexItem>
                ))}
              </EuiFlexGroup>
            </EuiFlexItem>
            {waveEntries.length > 0 && (
              <EuiFlexItem grow={false}>
                <EuiText size="xs" color="subdued">
                  {translate(`${i18n}.waves`, { defaultMessage: "Waves" })}
                </EuiText>
                <EuiSpacer size="xs" />
                <EuiFlexGroup gutterSize="xs" responsive={false} wrap>
                  {waveEntries.map(({ wave, count }) => (
                    <EuiFlexItem grow={false} key={wave}>
                      <EuiBadge color={waveColor(wave)}>
                        {wave} {count}
                      </EuiBadge>
                    </EuiFlexItem>
                  ))}
                </EuiFlexGroup>
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
          <EuiSpacer size="s" />
          {/*
            The detail charts and the secondary figures stay one click away
            instead of taking half the page on every visit.
          */}
          <EuiAccordion
            id="appliance-fleet-insights"
            buttonContent={translate(`${i18n}.insights`, {
              defaultMessage: "Insights",
            })}
            paddingSize="m"
          >
            <EuiFlexGroup gutterSize="l" wrap responsive={false}>
              {secondaryStats.map((stat) => (
                <EuiFlexItem grow={false} key={stat.label}>
                  <EuiFlexGroup
                    gutterSize="xs"
                    alignItems="baseline"
                    responsive={false}
                  >
                    <EuiFlexItem grow={false}>
                      <EuiText size="xs" color="subdued">
                        {stat.label}
                      </EuiText>
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                      <EuiText size="s">
                        <strong>{stat.value}</strong>
                      </EuiText>
                    </EuiFlexItem>
                  </EuiFlexGroup>
                </EuiFlexItem>
              ))}
            </EuiFlexGroup>
            <EuiHorizontalRule margin="m" />
            <EuiFlexGroup gutterSize="l" wrap>
              <EuiFlexItem style={{ minWidth: 300 }}>
                <FleetStatusStackedBar
                  fleet={fleet}
                  colors={colors}
                  loading={initialLoading}
                />
              </EuiFlexItem>
              <EuiFlexItem style={{ minWidth: 300 }}>
                <OpenIncidentsBar
                  byId={visibleById}
                  colors={colors}
                  loading={initialLoading}
                />
              </EuiFlexItem>
            </EuiFlexGroup>
            <EuiSpacer size="l" />
            <EuiFlexGroup gutterSize="l" wrap>
              <EuiFlexItem style={{ minWidth: 300 }}>
                <WaveDistributionDonut
                  waveCounts={provisioning.waveCounts}
                  colors={colors}
                />
              </EuiFlexItem>
              <EuiFlexItem style={{ minWidth: 300 }}>
                <AgentsBar rows={provisioning.agentRows} colors={colors} />
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiAccordion>
        </EuiPanel>
      </EuiFlexItem>
      {/*
        Physical NetBox hardware (fleet pool incl. free/spare devices) lives on
        its own page: opsanio > Hardware.
      */}
    </EuiFlexGroup>
  );
};

export default OpsanioApplianceOverview;
